import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Badge,
  Card,
  Collapse,
  Descriptions,
  Drawer,
  Empty,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';

import {
  useGetBsExamAnalytics,
  type BsExamAnalyticsBestFrame,
  type BsExamAnalyticsSweepSummary,
} from '../../../helpers/api/blind-sweep/bs-analytics';
import {
  STAGE_LABELS,
  formatDetectorOutcome,
  formatStage,
  getStageStatus,
  isWeakDetectorResult,
  stageChipColor,
  type StageKey,
} from './utils';

const { Text, Paragraph } = Typography;

interface BsExamAnalyticsDrawerProps {
  open: boolean;
  onClose: () => void;
  serieInstanceUid: string;
}

const POLL_MS = 5000;

function BsExamAnalyticsDrawer({
  open,
  onClose,
  serieInstanceUid,
}: BsExamAnalyticsDrawerProps) {
  const { data, isLoading, isFetching, refetch } = useGetBsExamAnalytics(
    serieInstanceUid,
    {
      enabled: open,
      refetchInterval: open ? POLL_MS : false,
    }
  );

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, serieInstanceUid, refetch]);

  const payload = data?.payload;
  const stages = payload?.stages;

  const stepItems = useMemo(() => {
    const keys: StageKey[] = ['autoselect', 'rules_enqueue', 'rules_execution'];
    return keys.map((key) => {
      const status = getStageStatus(payload, key);
      const at = formatStage(stages?.[key]);
      return {
        title: STAGE_LABELS[key],
        description: (
          <div className="flex flex-col gap-1">
            <Tag color={stageChipColor(status)}>{status}</Tag>
            {at ? <Text type="secondary">{at}</Text> : null}
          </div>
        ),
        status:
          status === 'completed' ||
          status === 'autofilled' ||
          status === 'already_filled'
            ? ('finish' as const)
            : status === 'failed'
              ? ('error' as const)
              : status === 'queued'
                ? ('process' as const)
                : ('wait' as const),
      };
    });
  }, [payload, stages]);

  const frameColumns: ColumnsType<BsExamAnalyticsBestFrame> = [
    { title: 'View', dataIndex: 'view_code', key: 'view_code' },
    {
      title: 'Frame',
      dataIndex: 'frame_id',
      key: 'frame_id',
      render: (v: string) => (
        <Text code className="text-xs">
          {v?.slice(0, 8)}…
        </Text>
      ),
    },
    { title: '#', dataIndex: 'frame_number', key: 'frame_number', width: 60 },
    {
      title: 'Quality %',
      dataIndex: 'quality_percentage',
      key: 'quality_percentage',
      width: 90,
    },
    { title: 'AF', dataIndex: 'af_score', key: 'af_score', width: 70 },
    {
      title: 'Confidence',
      dataIndex: 'confidence_score',
      key: 'confidence_score',
      width: 90,
    },
  ];

  const sweepColumns: ColumnsType<BsExamAnalyticsSweepSummary> = [
    {
      title: 'Sweep',
      dataIndex: 'sweep_index',
      key: 'sweep_index',
      width: 70,
      render: (idx: number) => (
        <span>
          {idx}
          {idx >= 5 && idx <= 7 ? (
            <Tag color="purple" className="ml-1">
              pres.
            </Tag>
          ) : null}
        </span>
      ),
    },
    { title: 'Frames', dataIndex: 'frame_count', key: 'frame_count', width: 80 },
    {
      title: 'w/ structures',
      dataIndex: 'frames_with_structures',
      key: 'frames_with_structures',
      width: 110,
    },
    {
      title: 'Detections',
      dataIndex: 'total_detections',
      key: 'total_detections',
      width: 100,
    },
    {
      title: 'Category IDs',
      dataIndex: 'category_ids',
      key: 'category_ids',
      render: (ids: number[]) =>
        (ids || []).slice(0, 12).map((id) => (
          <Tag key={id} className="mb-1">
            {id}
          </Tag>
        )),
    },
  ];

  return (
    <Drawer
      title="BS Pipeline Debug"
      placement="right"
      width={720}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Report best frames ≠ presentation rule inputs"
        description="Autoselect picks one frame per report view. Presentation rules scan all frames in sweeps 5–7 (and use all 7 sweeps for other detectors)."
      />

      {isLoading && !data ? (
        <div className="flex justify-center py-12">
          <Spin />
        </div>
      ) : !data ? (
        <Empty description="Analytics not ready yet (upload + wait for batch/rules)" />
      ) : (
        <div className="space-y-4">
          {isFetching ? (
            <Text type="secondary" className="text-xs">
              Refreshing…
            </Text>
          ) : null}

          {payload?.summary ? (
            <Paragraph className="mb-0">
              <Text strong>Summary: </Text>
              {payload.summary}
            </Paragraph>
          ) : null}

          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Serie">
              <Text code className="text-xs">
                {data.serie_instance_uid}
              </Text>
            </Descriptions.Item>
            {payload?.correlation_id ? (
              <Descriptions.Item label="Correlation">
                <Text code className="text-xs">
                  {payload.correlation_id}
                </Text>
              </Descriptions.Item>
            ) : null}
            {data.updated_at ? (
              <Descriptions.Item label="Updated">
                {new Date(data.updated_at).toLocaleString()}
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          <Steps direction="vertical" size="small" items={stepItems} />

          <div>
            <Text strong>Report best frames</Text>
            <Table
              size="small"
              className="mt-2"
              rowKey={(r) => `${r.view_code}-${r.frame_id}`}
              columns={frameColumns}
              dataSource={stages?.autoselect?.best_report_frames || []}
              pagination={false}
              locale={{ emptyText: 'No views autoselected yet' }}
              scroll={{ x: true }}
            />
          </div>

          <div>
            <Text strong>Rule engine sweep inputs</Text>
            <Table
              size="small"
              className="mt-2"
              rowKey="sweep_index"
              columns={sweepColumns}
              dataSource={stages?.rules_enqueue?.sweep_input_summary || []}
              pagination={false}
              locale={{ emptyText: 'Sweep summary pending' }}
              scroll={{ x: true }}
            />
          </div>

          <div>
            <Text strong>Detectors</Text>
            <div className="mt-2 space-y-2">
              {(stages?.rules_execution?.detectors || []).length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Waiting for rules execution" />
              ) : (
                (stages?.rules_execution?.detectors || []).map((detector) => (
                  <Card key={detector.name} size="small" title={detector.name}>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Text>{formatDetectorOutcome(detector)}</Text>
                      {isWeakDetectorResult(detector) ? (
                        <Badge status="warning" text="weak / empty" />
                      ) : (
                        <Badge status="success" text="result" />
                      )}
                    </div>
                    {detector.result?.method ? (
                      <Text code className="text-xs block whitespace-pre-wrap">
                        {String(detector.result.method)}
                      </Text>
                    ) : null}
                  </Card>
                ))
              )}
            </div>
          </div>

          <Collapse
            items={[
              {
                key: 'raw',
                label: 'Raw JSON',
                children: (
                  <pre className="text-xs overflow-auto max-h-80 bg-gray-50 p-2 rounded">
                    {JSON.stringify(data, null, 2)}
                  </pre>
                ),
              },
            ]}
          />
        </div>
      )}
    </Drawer>
  );
}

export default BsExamAnalyticsDrawer;
